import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { RivaledFullLogo } from "../components/logos/RivaledFullLogo";
import { Button, Checkbox, Input, PinInput } from "../components/ui";
import { authClient } from "../lib/auth-client";

export const Route = createFileRoute("/2fa")({
  component: TwoFactorPage,
});

function TwoFactorPage() {
  const navigate = useNavigate();
  const [method, setMethod] = useState<"totp" | "backup">("totp");
  const [code, setCode] = useState("");
  const [backupCode, setBackupCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [trustDevice, setTrustDevice] = useState(true);

  const handleVerificationError = (message: string) => {
    setError(message);
    if (method === "totp") {
      setCode("");
    } else {
      setBackupCode("");
    }
    setIsLoading(false);
  };

  const submitTotpCode = async (fullCode: string) => {
    setError(null);
    setIsLoading(true);

    const { error: verifyError } = await authClient.twoFactor.verifyTotp({
      code: fullCode,
      trustDevice,
    });

    if (verifyError) {
      handleVerificationError(verifyError.message ?? "Invalid code");
    } else {
      void navigate({ to: "/app" });
    }
  };

  const submitBackupCode = async () => {
    setError(null);
    setIsLoading(true);

    const { error: verifyError } = await authClient.twoFactor.verifyBackupCode({
      code: backupCode.trim(),
      trustDevice,
    });

    if (verifyError) {
      handleVerificationError(verifyError.message ?? "Invalid backup code");
    } else {
      void navigate({ to: "/app" });
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (method === "totp") {
      if (code.length === 6) {
        void submitTotpCode(code);
      }
      return;
    }

    if (backupCode.trim().length > 0) {
      void submitBackupCode();
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-surface-950">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_0%,rgba(38,217,181,0.06),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_100%,rgba(38,217,181,0.03),transparent_50%)]" />
        <div className="absolute inset-0 noise-overlay" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="relative mx-4 w-full max-w-md"
      >
        <div className="mb-10 flex justify-center">
          <Link to="/">
            <RivaledFullLogo className="h-10 opacity-80 transition-opacity hover:opacity-100" />
          </Link>
        </div>

        <div className="relative overflow-hidden rounded-2xl border border-surface-700/50 bg-surface-900/80 backdrop-blur-sm">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-500/40 to-transparent" />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(38,217,181,0.04)_0%,_transparent_60%)]" />

          <form onSubmit={handleSubmit} className="relative px-8 py-10">
            <div className="mb-8 text-center">
              <div className="mb-4 flex justify-center">
                <div className="flex size-12 items-center justify-center rounded-full border border-brand-500/20 bg-brand-500/10">
                  <ShieldCheck className="size-6 text-brand-400" />
                </div>
              </div>
              <h1 className="font-display text-2xl font-bold uppercase tracking-tight text-white">
                Verification Required
              </h1>
              <p className="mt-2 text-sm text-surface-400">
                {method === "totp"
                  ? "Enter the 6-digit code from your authenticator app"
                  : "Enter one of your saved backup codes"}
              </p>
            </div>

            <AnimatePresence mode="wait">
              {method === "totp" ? (
                <motion.div
                  key="totp"
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  transition={{ duration: 0.15 }}
                >
                  <PinInput
                    length={6}
                    value={code}
                    onChange={setCode}
                    onComplete={submitTotpCode}
                    disabled={isLoading}
                    autoFocus
                    error={error ?? undefined}
                    className="mb-5"
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="backup"
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  transition={{ duration: 0.15 }}
                >
                  <Input
                    label="Backup Code"
                    value={backupCode}
                    onChange={(event) => setBackupCode(event.target.value)}
                    autoComplete="one-time-code"
                    autoFocus
                    error={error ?? undefined}
                    placeholder="e.g. a1b2c-d3e4f"
                    className="mb-5"
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <Checkbox
              label="Trust this device for 30 days"
              checked={trustDevice}
              onChange={(e) => setTrustDevice(e.currentTarget.checked)}
              className="mb-6"
            />

            <Button
              type="submit"
              variant="primary"
              disabled={isLoading || (method === "totp" ? code.length !== 6 : !backupCode.trim())}
              className="w-full py-2.5"
              leftIcon={isLoading ? <Loader2 className="size-4 animate-spin" /> : undefined}
            >
              {method === "totp" ? "Verify" : "Use Backup Code"}
            </Button>

            <div className="mt-6 flex items-center justify-center gap-3 text-sm">
              {method === "totp" ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 text-surface-500 transition-colors hover:text-surface-300"
                  onClick={() => {
                    setError(null);
                    setCode("");
                    setBackupCode("");
                    setMethod("backup");
                  }}
                >
                  <KeyRound className="size-3.5" />
                  Use a backup code
                </button>
              ) : (
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 text-surface-500 transition-colors hover:text-surface-300"
                  onClick={() => {
                    setError(null);
                    setCode("");
                    setBackupCode("");
                    setMethod("totp");
                  }}
                >
                  <ShieldCheck className="size-3.5" />
                  Use authenticator app
                </button>
              )}
              <span className="text-surface-700">|</span>
              <Link
                to="/sign-in"
                className="text-surface-500 transition-colors hover:text-surface-300"
              >
                Different account
              </Link>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
